/**
 * Copyright © Smartex Canada All rights reserved.
 * See LICENSE for license details.
 */

/**
 * Returns all focusable elements within provided root element.
 */
export function getFocusableElements(root: HTMLElement): HTMLElement[] {
    return Array.from(root.querySelectorAll<HTMLElement>(FocusTrap.focusableSelector)).filter(element => {
        const isDisabledOrHidden = element.getAttribute('aria-disabled') === 'true'
            || element.getAttribute('disabled') != null
            || element.getAttribute('hidden') != null
            || element.getAttribute('aria-hidden') === 'true';
        const isTabbableAndVisible = element.tabIndex >= 0
            && element.getBoundingClientRect().width > 0
            && !element.classList.contains(FocusTrap.focusDelimiterClass)
            && !isDisabledOrHidden;

        let isHidden: boolean = false;
        if (isTabbableAndVisible) {
            const style = getComputedStyle(element);
            isHidden = style.display === 'none' || style.visibility === 'hidden';
        }
        return isTabbableAndVisible && !isHidden;
    });
}

export class FocusTrap {
    static readonly focusableSelector = '[autofocus], [tabindex], a, button, input, select, textarea, details';
    static readonly focusDelimiterClass = 'focus-trap__delimiter';

    private readonly root: HTMLElement;
    private readonly options: FocusTrapOptions;

    private previousFocusElement: HTMLElement | null = null;
    private startDelimiter: HTMLElement | null = null;
    private endDelimiter: HTMLElement | null = null;

    constructor(root: HTMLElement, options: FocusTrapOptions = {}) {
        this.root = root;
        this.options = options;
    }

    /**
     * Traps focus in root element, sets focus to inital focus element,
     * or first focusable child element.
     */
    public trapFocus(): void {
        const focusableElements = getFocusableElements(this.root);
        if (focusableElements.length === 0) {
            return;
        }

        this.previousFocusElement = document.activeElement instanceof HTMLElement
            ? document.activeElement
            : null;

        this.wrapTabFocus(this.root);

        if (!this.options.skipInitialFocus) {
            this.focusInitialElement(focusableElements, this.options.initialFocusElement);
        }
    }

    /**
     * Releases focus from root element and restores focus to previously
     * focused element.
     */
    public releaseFocus(): void {
        this.startDelimiter?.remove();
        this.startDelimiter = null;

        this.endDelimiter?.remove();
        this.endDelimiter = null;

        if (!this.options.skipRestoreFocus && this.previousFocusElement) {
            this.previousFocusElement.focus();
            this.previousFocusElement = null;
        }
    }

    /**
     * Wraps tab focus within `element`.
     */
    private wrapTabFocus(element: HTMLElement): void {
        this.startDelimiter = this._createFocusDelimiter();
        this.endDelimiter = this._createFocusDelimiter();

        this.startDelimiter.addEventListener('focus', () => {
            const focusableElements = getFocusableElements(element);
            if (focusableElements.length > 0) {
                focusableElements[focusableElements.length - 1].focus();
            }
        });
        this.endDelimiter.addEventListener('focus', () => {
            const focusableElements = getFocusableElements(element);
            if (focusableElements.length > 0) {
                focusableElements[0].focus();
            }
        });

        element.prepend(this.startDelimiter);
        element.append(this.endDelimiter);
    }

    /**
     * Focuses on `initialFocusElement` if defined and a child of the root element.
     * Otherwise, focuses on the first focusable child element.
     */
    private focusInitialElement(focusableElements: HTMLElement[], initialFocusElement?: HTMLElement | null) {
        let focusIndex = 0;
        if (initialFocusElement) {
            focusIndex = Math.max(focusableElements.indexOf(initialFocusElement), 0);
        }
        focusableElements[focusIndex].focus();
    }

    private _createFocusDelimiter(): HTMLElement {
        const element = document.createElement('div');

        element.setAttribute('tabindex', '0');
        element.setAttribute('aria-hidden', 'true');
        element.classList.add(FocusTrap.focusDelimiterClass);
        return element;
    }
}

export interface FocusTrapOptions {
    initialFocusElement?: HTMLElement | null;
    skipInitialFocus?: boolean;
    skipRestoreFocus?: boolean;
}
